import { useAccountService } from "ls/features/user/useAccountService";
import { isSubInstance } from "ls/state/useSubInstance";
import { ref } from "vue";

export interface ExternalLinkWarningRequest {
  url: string;
  resolve: (opened: boolean) => void;
}

/** Pending external-link confirmation; consumed by ExternalLinkWarningHost. */
export const pendingExternalLinkWarning = ref<ExternalLinkWarningRequest | null>(null);

function openInNewTab(url: string) {
  window.open(url, "_blank", "noopener,noreferrer");
}

/**
 * True when the URL is http(s) and points outside the current Cloud origin.
 * Non-http(s) schemes (mailto, tel, etc.) are not considered external Cloud exits.
 */
export function isExternalCloudUrl(url: string): boolean {
  try {
    const parsed = new URL(url, globalThis.location.origin);
    if (parsed.protocol !== "http:" && parsed.protocol !== "https:") {
      return false;
    }
    return parsed.origin !== globalThis.location.origin;
  } catch {
    return false;
  }
}

/**
 * Opens a URL in a new tab. Same-origin and non-http(s) links open immediately.
 * External http(s) links prompt for confirmation only for anonymous / public viewers
 * on public Cloud (the same unauthenticated case where DocumentViewHeader shows the Axure logo).
 * Signed-in users and non-public Cloud deployments (ACB / on-prem) open external links without a warning.
 * @returns true if the URL was opened, false if cancelled or invalid.
 */
export function openUrl(url: string): Promise<boolean> {
  if (!url) {
    return Promise.resolve(false);
  }

  if (!isExternalCloudUrl(url)) {
    openInNewTab(url);
    return Promise.resolve(true);
  }

  const { isAuthenticated } = useAccountService();
  if (isAuthenticated.value) {
    openInNewTab(url);
    return Promise.resolve(true);
  }

  if (isSubInstance()) {
    openInNewTab(url);
    return Promise.resolve(true);
  }

  // Replace any in-flight confirmation so only one dialog is active
  const previous = pendingExternalLinkWarning.value;
  if (previous) {
    previous.resolve(false);
  }

  return new Promise<boolean>(resolve => {
    pendingExternalLinkWarning.value = {
      url,
      resolve: opened => {
        pendingExternalLinkWarning.value = null;
        if (opened) {
          openInNewTab(url);
        }
        resolve(opened);
      },
    };
  });
}

export function confirmExternalLinkWarning() {
  pendingExternalLinkWarning.value?.resolve(true);
}

export function cancelExternalLinkWarning() {
  pendingExternalLinkWarning.value?.resolve(false);
}
